// ./src/utils/api/EventRentalService.ts
//
// Event-rental service — a per-event rental of an asset, with pickup/drop-off each
// carrying its own IANA timezone, a derived total cost, an optional linked budget
// line, and lifecycle status. OpResult-native, event-scoped. Full single-item CRUD,
// a bulk status/category/responsible change or remove (array of ids in the where),
// and a `summary` query with the event's rolled-up totals. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";
import { RentalVenueRef } from "./RentalAssetService";

// Pricing-model constants live in RentalAssetService (the source of truth); import
// them from there when a rental form needs them.

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/rental.ts)
// =============================================================================

export const RENTAL_STATUS = {
  RESERVED: "reserved",
  OUT: "out",
  RETURNED: "returned",
  CANCELED: "canceled",
} as const;

export const RENTAL_STATUS_VALUES = Object.values(RENTAL_STATUS);

export const RENTAL_STATUS_LABELS: Record<string, string> = {
  reserved: "Reserved", // booked, not yet in our hands
  out: "In possession", // we have it (picked up OR delivered)
  returned: "Returned", // gone back (returned OR collected)
  canceled: "Canceled",
};

/** One-line meaning of each rental status, for pickers / tooltips. */
export const RENTAL_STATUS_DESCRIPTIONS: Record<string, string> = {
  reserved: "Booked — not in our hands yet",
  out: "We have it — picked up or delivered",
  returned: "Gone back — returned or collected",
  canceled: "Rental did not go ahead",
};

export const RENTAL_PARTY_KIND = {
  USER: "user",
  CREW: "crew",
  GUEST: "guest",
  VENDOR: "vendor",
  VENUE: "venue",
} as const;

export const RENTAL_PARTY_KIND_VALUES = Object.values(RENTAL_PARTY_KIND);

// How the item moves at each end (per-end, independent).
export const RENTAL_PICKUP_FULFILLMENT = {
  SELF: "self", // we collect it (location = the vendor's place)
  DELIVERY: "delivery", // vendor delivers it (location = our delivery address)
} as const;

export const RENTAL_DROPOFF_FULFILLMENT = {
  SELF: "self", // we return it (location = the vendor's place)
  COLLECTION: "collection", // vendor collects it (location = the address they take it from)
} as const;

export const RENTAL_PICKUP_FULFILLMENT_LABELS: Record<string, string> = {
  self: "We pick up",
  delivery: "They deliver",
};

export const RENTAL_DROPOFF_FULFILLMENT_LABELS: Record<string, string> = {
  self: "We return",
  collection: "They collect",
};

// =============================================================================
// GraphQL
// =============================================================================

const RENTAL_MEDIA_SUBFIELDS = `
  id
  eventRentalId
  uploadedFileId
  kind
  phase
  caption
  orderNo
  status
  rights
  file { id kind name originalFilename mimeType sizeBytes fileUrl thumbnailUrl }
`;

const EVENT_RENTAL_FIELDS = `
  id
  eventId
  accountId
  rentalAssetId
  categoryId
  name
  description
  vendorId
  vendorLabel
  vendor { id name }
  pricingModel
  unitAmount
  quantity
  currencyCode
  totalAmount
  trackInBudget
  budgetItemId
  pickupFulfillment
  dropoffFulfillment
  pickupVenueId
  pickupVenue { id name }
  pickupLocationName
  pickupAddress
  pickupPlaceId
  pickupLatitude
  pickupLongitude
  pickupAt
  pickupTimezone
  dropoffVenueId
  dropoffVenue { id name }
  dropoffLocationName
  dropoffAddress
  dropoffPlaceId
  dropoffLatitude
  dropoffLongitude
  dropoffAt
  dropoffTimezone
  rentalStatus
  responsibleKind
  responsibleId
  responsibleLabel
  notes
  orderNo
  status
  rights
  media { ${RENTAL_MEDIA_SUBFIELDS} }
  createdAt
  updatedAt
`;

const EVENT_RENTAL_LIST_QUERY = gql`
  query EventRentalList($filter: EventRentalFilter, $params: PaginationAndSorting) {
    eventRentalList(filter: $filter, params: $params) {
      code
      data { ${EVENT_RENTAL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_RENTAL_GET_QUERY = gql`
  query EventRentalGet($id: ID) {
    eventRentalGet(id: $id) {
      code
      data { ${EVENT_RENTAL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_RENTAL_CREATE_MUTATION = gql`
  mutation EventRentalCreate($params: EventRentalInput) {
    eventRentalCreate(params: $params) {
      code
      data { ${EVENT_RENTAL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_RENTAL_UPDATE_MUTATION = gql`
  mutation EventRentalUpdate($where: EventRentalWhereInput, $params: EventRentalInput) {
    eventRentalUpdate(where: $where, params: $params) {
      code
      data { ${EVENT_RENTAL_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_RENTAL_REMOVE_MUTATION = gql`
  mutation EventRentalRemove($where: EventRentalWhereInput) {
    eventRentalRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const EVENT_RENTAL_SUMMARY_QUERY = gql`
  query EventRentalSummary($filter: EventRentalFilter) {
    eventRentalSummary(filter: $filter) {
      code
      data { rentalCount totalCost currencyCode reservedCount outCount returnedCount canceledCount }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventRental schema)
// =============================================================================

export interface RentalMediaFile {
  id?: string;
  kind?: string | null;
  name?: string | null;
  originalFilename?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  fileUrl?: string | null;
  thumbnailUrl?: string | null;
}

export interface RentalMedia {
  id?: string;
  eventRentalId?: string | null;
  uploadedFileId?: string | null;
  kind?: string | null;
  phase?: string | null;
  caption?: string | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  file?: RentalMediaFile | null;
}

export interface EventRental {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  rentalAssetId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  /** Vendor — soft ref → venue (kind='vendor'). */
  vendorId?: string | null;
  /** Fallback name snapshot — used only if the vendor was removed. */
  vendorLabel?: string | null;
  /** The vendor venue resolved live (current name); null if removed. */
  vendor?: RentalVenueRef | null;
  pricingModel?: string | null;
  unitAmount?: number | null;
  quantity?: number | null;
  currencyCode?: string | null;
  /** DERIVED server-side: the total cost. */
  totalAmount?: number | null;
  trackInBudget?: boolean | null;
  budgetItemId?: string | null;
  /** self (we collect) | delivery (vendor delivers to our address). */
  pickupFulfillment?: string | null;
  /** self (we return) | collection (vendor collects from our address). */
  dropoffFulfillment?: string | null;
  pickupVenueId?: string | null;
  /** The pickup venue resolved live (current name); null in address mode or if removed. */
  pickupVenue?: RentalVenueRef | null;
  pickupLocationName?: string | null;
  pickupAddress?: string | null;
  pickupPlaceId?: string | null;
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
  pickupAt?: string | null;
  pickupTimezone?: string | null;
  dropoffVenueId?: string | null;
  /** The drop-off venue resolved live (current name); null in address mode or if removed. */
  dropoffVenue?: RentalVenueRef | null;
  dropoffLocationName?: string | null;
  dropoffAddress?: string | null;
  dropoffPlaceId?: string | null;
  dropoffLatitude?: number | null;
  dropoffLongitude?: number | null;
  dropoffAt?: string | null;
  dropoffTimezone?: string | null;
  rentalStatus?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
  responsibleLabel?: string | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  media?: RentalMedia[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface EventRentalInput {
  eventId?: string | null;
  rentalAssetId?: string | null;
  categoryId?: string | null;
  name?: string | null;
  description?: string | null;
  vendorId?: string | null;
  pricingModel?: string | null;
  unitAmount?: number | null;
  quantity?: number | null;
  trackInBudget?: boolean | null;
  pickupFulfillment?: string | null;
  dropoffFulfillment?: string | null;
  pickupVenueId?: string | null;
  pickupLocationName?: string | null;
  pickupAddress?: string | null;
  pickupPlaceId?: string | null;
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
  pickupAt?: string | null;
  pickupTimezone?: string | null;
  dropoffVenueId?: string | null;
  dropoffLocationName?: string | null;
  dropoffAddress?: string | null;
  dropoffPlaceId?: string | null;
  dropoffLatitude?: number | null;
  dropoffLongitude?: number | null;
  dropoffAt?: string | null;
  dropoffTimezone?: string | null;
  rentalStatus?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

// A bulk update (>1 id) may mass-set status, category or responsible.
export interface EventRentalBulkInput {
  rentalStatus?: string | null;
  categoryId?: string | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
}

export interface EventRentalFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  rentalAssetId?: string[] | null;
  categoryId?: string[] | null;
  rentalStatus?: string[] | null;
  responsibleKind?: string[] | null;
  responsibleId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface EventRentalWhereInput {
  id?: string | string[];
}

export interface EventRentalSummary {
  rentalCount?: number | null;
  totalCost?: number | null;
  currencyCode?: string | null;
  reservedCount?: number | null;
  outCount?: number | null;
  returnedCount?: number | null;
  canceledCount?: number | null;
}

// =============================================================================
// Service
// =============================================================================

export class EventRentalService {
  static async list(
    filter?: EventRentalFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_RENTAL_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Every row matching `filter`, paged — for PRINT SHEETS and exports, which need the whole set.
   * ⚠ Never pass a literal `pageSize` in a print page instead of this: paper has no Load-more
   * button, so a capped sheet is indistinguishable from a complete one. See `listAllPages`.
   */
  static async listAll(filter?: EventRentalFilter, sortBy?: SortItem[], opts?: ListAllOptions): Promise<OpResult> {
    return listAllPages<EventRental>(EventRentalService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_RENTAL_GET_QUERY, { id });
  }

  static async create(params: EventRentalInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_RENTAL_CREATE_MUTATION, { params });
  }

  static async update(where: EventRentalWhereInput, params: EventRentalInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_RENTAL_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_RENTAL_REMOVE_MUTATION, { where: { id } });
  }

  /** The event's rolled-up rental totals across every rental. */
  static async summary(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_RENTAL_SUMMARY_QUERY, { filter: { eventId: [eventId] } });
  }

  /** Mass-set status / category / responsible on every selected rental in one call. */
  static async bulkUpdate(ids: string[], params: EventRentalBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(EVENT_RENTAL_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected rental in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(EVENT_RENTAL_REMOVE_MUTATION, { where: { id: ids } });
  }
}
